import {Component, HostListener, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {DashboardService} from '../../../../domain/dashboard/service/dashboard.service';
import {Dashboard} from '../../../../domain/dashboard/model/dashboard';
import {SafeSubscriberDirective} from '../../../../application/app-context/safe-subscriber.directive';
import {WidgetDetail} from '../../../../domain/widget/model/widget-detail';
import {ChangeEventArgs, PanelModel} from '@syncfusion/ej2-layouts';
import {DashboardLayoutComponent} from '@syncfusion/ej2-angular-layouts';
import {WidgetSize} from '../../../../domain/widget/model/widget-size.enum';
import {LocalStorageKeyEnum} from '../../../../application/app-context/local-storage-key.enum';
import {getPositionOfWidget, updateWidgetOrder} from '../../../../domain/widget/widget.util';
import {UserContext} from '../../../../application/app-context/user-context/user-context';
import {UserContextService} from '../../../../application/app-context/user-context/user-context.service';
import {MatDialog} from '@angular/material/dialog';
import {ListAdvertisingDialogComponent} from '../advertising/dialog/list/list-advertising-dialog.component';
import {ConfigService} from '../../../../application/config/config.service';
import {Role} from '../../../../domain/core/model/role';
import {DomainUrlService} from '../../../../application/config/domain-url.service';
import {DialogEventsComponent} from '../agenda/dialog-events/dialog-events.component';
import {WidgetService} from '../../../../domain/widget/service/widget.service';
import {HomeDashboardWidgetCatalogDialogComponent} from './available-widgets/home-dashboard-widget-catalog-dialog.component';

@Component({
             selector: 'app-dashboard',
             templateUrl: './home-dashboard.component.html',
             styleUrls: ['./home-dashboard.component.scss'],
             encapsulation: ViewEncapsulation.None
           })
export class HomeDashboardComponent extends SafeSubscriberDirective implements OnInit {

  @ViewChild('defaultLayout') dashboardLayout: DashboardLayoutComponent;

  dashboard: Dashboard | undefined;
  isOnEditView = false;
  isBackOfficeUser: boolean;
  cellAspectRatio: number;
  cellSpacing = 20;
  columns;
  userContext: UserContext | undefined;
  isAdvertisingAdministrator: boolean | undefined;
  toggleDemoMode = false;
  isConsultant: boolean | undefined;
  isProd: boolean | undefined;

  constructor(public dialog: MatDialog,
              private dashboardService: DashboardService,
              private widgetService: WidgetService,
              private configService: ConfigService,
              private userContextService: UserContextService,
              private domainUrlService: DomainUrlService) {
    super();
  }

  ngOnInit(): void {
    this.setScreenResolution();
    this.userContext = this.userContextService.getUserContext();
    this.isConsultant = Role.isConsultant(this.userContext.user.role);
    this.isProd = this.domainUrlService.isProdDomain();
    this.isAdvertisingAdministrator = this.initIsAdvertisingAdministrator();
    this.toggleDemoMode = localStorage.getItem(LocalStorageKeyEnum.DEMO_MODE) === 'on';
    this.isBackOfficeUser = this.initIsBackOffice(this.userContext);

    this.subs.push(
      this.dashboardService.getDashboardWidgetStructure()
          .subscribe((result: Dashboard) => this.dashboard = result),
      this.widgetService.updateWidgetsSubject
          .subscribe((newWidget: WidgetDetail) => {
            this.addWidgetToDashboard(newWidget);
          })
    );
  }

  public onToggleDemoMode(): void {
    this.toggleDemoMode = !this.toggleDemoMode;
    localStorage.setItem(LocalStorageKeyEnum.DEMO_MODE, this.toggleDemoMode ? 'on' : 'off');
  }

  public openManageAdvertisingDialog(): void {
    this.dialog.open(ListAdvertisingDialogComponent, {
      id: 'ListAdvertisingDialogComponent',
      width: '1000px',
      height: '534px',
      data: {}
    });

  }

  private initIsBackOffice(userContext: UserContext): boolean {
    if (this.configService.config.isAgendaEventAdministrationEnabledForAll) {
      return true;
    }

    return this.configService.config.agendaEventAdministratorUserLoginList
               .split(',')
               .some(v => v === userContext?.user.login);
  }


  openManageEventAdministrationDialog() {
    this.dialog.open(DialogEventsComponent, {
      id: 'DialogEvents',
      width: '600px',
      height: '100vh',
      position: {
        right: '0'
      },
      data: {}
    });
  }

  openAvailableWidgetCatalog() {
    this.dialog.open(HomeDashboardWidgetCatalogDialogComponent, {
      id: 'AvailableWidgetCatalog',
      width: '40rem',
      height: '100vh',
      position: {
        right: '0'
      },
      data: {
        dashboardId: this.dashboard.id
      }
    });
  }

  private initIsAdvertisingAdministrator(): boolean {
    return this.configService.config.advertisingAdministratorUserLoginList.some(v => v === this.userContext?.user.login)
           || (Role.isConsultant(this.userContext?.user.role) && this.configService.config.isAdvertisingAdministrationEnabledForAll);
  }

  public addWidgetToDashboard(widgetToAdd: WidgetDetail): void {
    this.dashboard.widgets.push(widgetToAdd);
    this.checkLargesWidgetsPositions();
    const newPanel: PanelModel = {
      id: widgetToAdd.id,
      sizeX: this.getSize(widgetToAdd.size, true),
      sizeY: this.getSize(widgetToAdd.size, false),
      row: this.getPosition(widgetToAdd, true),
      col: this.getPosition(widgetToAdd, false),
      zIndex: 3
    };
    this.dashboardLayout.addPanel(newPanel); // need to add panel to have position after floating
    this.saveWidgetsOrders(this.getWidgetToUpdate(this.dashboardLayout.panels)); // need to update orders because of floating
    this.dashboardLayout.removePanel(newPanel.id); // need to remove panel because we don't know how put component in content in typescript
  }

  public removeWidgetFromDashboard(widget: WidgetDetail): void {
    if (this.dashboard && widget) {
      const widgetIndex = this.dashboard.widgets.indexOf(widget);
      this.dashboard.widgets.splice(widgetIndex, 1);
      if (widget.id) {
        this.dashboardLayout.removePanel(widget.id);
      }
    }
  }

  public onPanelsChange(args: ChangeEventArgs): void {
    const changedPanels: Array<PanelModel> = args.changedPanels;
    if (changedPanels.length > 0) {
      const updatedWidgetsOrders = this.getWidgetToUpdate(changedPanels);
      this.saveWidgetsOrders(updatedWidgetsOrders);
    }
  }

  public getSize(widgetSize: WidgetSize, isXValue: boolean): number {
    switch (widgetSize) {
      case WidgetSize.SMALL:
        return 1;
      case WidgetSize.LARGE:
        return isXValue ? 2 : 1;
      case WidgetSize.XLARGE:
        return 2;
      default:
        return undefined;
    }
  }

  public getPosition(widget: WidgetDetail, isXValue: boolean): number {
    const positionOfWidget = getPositionOfWidget(widget, this.columns);
    return isXValue ? positionOfWidget.x : positionOfWidget.y;
  }

  public toggleEditView(): void {
    this.isOnEditView = !this.isOnEditView;
    this.refresh();
  }

  public reinitializeDashboard(): void {
    // Crapy : if dashboard contains same number of panels than the default, we have to remove widgets for refreshing their contents
    this.dashboard.widgets.sort((w1, w2) => w2.order - w1.order).forEach(widget => this.removeWidgetFromDashboard(widget));

    this.subs.push(
      this.dashboardService.reinitializeDashboard(this.dashboard.id)
          .subscribe((result: Dashboard) => {
            this.dashboard = result;
            this.toggleEditView();
          })
    );
  }

  private getWidgetToUpdate(changedPanels: Array<PanelModel>): Array<WidgetDetail> {
    return changedPanels.map((panel: PanelModel) => {
      return updateWidgetOrder(this.getCurrentWidget(panel), this.columns, panel);
    });
  }

  private getCurrentWidget(panel: PanelModel): WidgetDetail {
    return this.dashboard.widgets.filter((widget: WidgetDetail) => widget.id === panel.id).shift();
  }

  private saveWidgetsOrders(updatedWidgetsOrders: Array<WidgetDetail>): void {
    this.subs.push(
      this.dashboardService.saveWidgetOrders(this.dashboard.id, updatedWidgetsOrders)
          .subscribe(result => {
            if (result) {
              this.updateCurrentDashboard(updatedWidgetsOrders);
            }
          })
    );
  }

  private updateCurrentDashboard(updatedWidgetsOrders: Array<WidgetDetail>): void {
    updatedWidgetsOrders.forEach((widget: WidgetDetail) => {
      this.dashboard.widgets.filter(widgetToUpdate => widgetToUpdate.id === widget.id).shift().order = widget.order;
    });
  }

  // Check if a large widget has its first position in last column
  // If it's the case then we shift this widget's order and all orders following
  private checkLargesWidgetsPositions(): void {
    if (this.dashboard && this.dashboard.widgets) {
      let shift = 0;
      this.dashboard.widgets
          .sort((w1, w2) => w1.order - w2.order)
          .forEach(widget => {
            if (widget.size === WidgetSize.LARGE && this.getPosition(widget, false) === this.columns - 1) {
              shift++;
            }
            if (shift > 0) {
              widget.order += shift;
            }
          });
    }
  }

  private updatePanels(): void {
    this.dashboardLayout?.panels.forEach(panel => {
      const widgetDetail = this.dashboard.widgets.filter(widget => widget.id === panel.id).shift();
      panel.row = this.getPosition(widgetDetail, true);
      panel.col = this.getPosition(widgetDetail, false);
      this.dashboardLayout.updatePanel(panel);
    });
  }

  private updateAndSaveWidgetOrders(updatedWidgetsOrders: Array<WidgetDetail>): void {
    this.updateCurrentDashboard(updatedWidgetsOrders);
    this.saveWidgetsOrders(updatedWidgetsOrders);
  }

  @HostListener('window:resize')
  private setScreenResolution(): void {
    if (window.innerWidth <= 1280 && this.columns !== 4) {
      if (this.dashboardLayout && this.dashboardLayout.panels) {
        this.updateAndSaveWidgetOrders(this.getWidgetToUpdate(this.dashboardLayout.panels)); // need to update orders because of floating
      }
      this.columns = 4;
      this.checkLargesWidgetsPositions();
      setTimeout(() => this.updatePanels(), 10);
    }
    if (window.innerWidth > 1280 && this.columns !== 6) {
      this.columns = 6;
      this.checkLargesWidgetsPositions();
      setTimeout(() => this.updatePanels(), 10);
    }
    this.refresh();
  }

  private refresh(): void {
    this.updateRatio();
    if (this.dashboardLayout) {
      this.dashboardLayout.cellAspectRatio = this.cellAspectRatio;
      this.dashboardLayout.refresh();
    }
  }

  private updateRatio(): void {
    const dashboardContainerWidth = document.getElementById('defaultLayout-container').offsetWidth;
    const widgetHeight = 190;
    this.cellAspectRatio = (dashboardContainerWidth - (3 * this.cellSpacing)) / this.columns / widgetHeight;
  }
}
